import { Checkbox, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { dateLabelFormatDay, dateLabelFormatDayWithYear } from '@hero/constants';
import { Table } from '@hero/molecules';
import {
  useGetAllContactIds,
  useGetContacts,
  useGetTagList
} from '@hero/services/contact-service/hooks';
import { checkFormatContainsYear } from '@hero/utils/dateUtils';

import { CustomRemoveContactEmptyTable } from './CustomRemoveContactEmptyTable';
import { RemoveContactCustomSelectionHead } from './RemoveContactCustomSelectionHead';
import * as S from './style';

function RemoveContactTable({
  selectedRows = {},
  totalContactData,
  onChangeSelectedRows,
  translations = {},
  disabledColumns = []
}) {
  const { t } = useTranslation();

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});
  const [rowSelection, setRowSelection] = useState(selectedRows || {});

  const { data: searchedIds, isLoading: isLoadingSearchedIds } = useGetAllContactIds(
    {
      filters: {
        searchTerm: globalFilters.searchTerm,
        tags: globalFilters.filters?.map(filter => filter.value)
      },
      options: {
        exists: [{}]
      }
    },
    {
      cacheTime: Infinity,
      enabled: !!globalFilters?.searchTerm || globalFilters?.filters?.length > 0
    }
  );

  const filteredContactData = useMemo(() => {
    return totalContactData.filter(
      contactId =>
        !searchedIds?.docs?.length ||
        searchedIds?.docs.filter(doc => doc.id === contactId).length > 0
    );
  }, [totalContactData, searchedIds]);

  const contactsByPage = useMemo(() => {
    let pageIndex = 0;
    const result = {};

    const pageCount = Math.ceil(filteredContactData.length / pageSize);

    while (pageIndex < pageCount) {
      result[pageIndex] = filteredContactData.slice(
        pageIndex * pageSize,
        pageIndex * pageSize + pageSize
      );
      pageIndex += 1;
    }

    return result;
  }, [filteredContactData, pageSize]);

  const { data: filterOptions } = useGetTagList();
  const { data: filteredContacts, isLoading: isLoadingContacts } = useGetContacts({
    filters: {
      searchTerm: globalFilters.searchTerm,
      ids: contactsByPage[pageIndex]
    },
    pagination: {
      page: 1,
      limit: pageSize
    }
  });

  const contacts = filteredContactData.length > 0 ? filteredContacts : [];

  const pageCount = useMemo(() => {
    return Object.keys(contactsByPage || {}).length;
  }, [contactsByPage]);

  const rowSelectionArray = useMemo(() => {
    return Object.keys(rowSelection || {});
  }, [rowSelection]);

  const columns = useMemo(
    () =>
      [
        {
          id: 'select',
          maxSize: 30,
          header: ({ table }) => {
            return (
              <Checkbox
                {...{
                  key: table.getState()?.pagination?.pageIndex,
                  checked: table.getIsAllPageRowsSelected(),
                  indeterminate: table.getIsSomePageRowsSelected(),
                  onChange: table.getToggleAllPageRowsSelectedHandler()
                }}
              />
            );
          },
          cell: ({ row }) => {
            return (
              <Checkbox
                {...{
                  name: 'remove_' + row.original?.id,
                  checked: row.getIsSelected(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler()
                }}
              />
            );
          }
        },
        {
          id: 'email',
          header: t('email'),
          size: 200,
          accessorKey: 'email',
          cell: ({
            row: {
              original: { email }
            }
          }) => <S.TableEmail>{email}</S.TableEmail>
        },
        {
          header: t('name'),
          size: 200,
          accessorKey: 'firstName',
          cell: ({
            row: {
              original: { firstName, lastName }
            }
          }) => (
            <S.TableName role="button" tabIndex={0}>
              {`${firstName || ''} ${lastName || ''}`}
            </S.TableName>
          )
        },
        {
          id: 'birthday',
          header: t('birthday'),
          editable: false,
          size: 200,
          type: 'mask',
          accessorFn: e => e.birthday?.full,
          componentProps: {
            formatPreview: v => {
              return v
                ? dayjs(v).format(
                    checkFormatContainsYear(v) ? dateLabelFormatDayWithYear : dateLabelFormatDay
                  )
                : '';
            }
          }
        },
        {
          id: 'anniversary',
          header: t('workAnniversary'),
          editable: false,
          size: 200,
          type: 'mask',
          accessorFn: e => e.anniversary?.full,
          componentProps: {
            formatPreview: v => {
              return v
                ? dayjs(v).format(
                    checkFormatContainsYear(v) ? dateLabelFormatDayWithYear : dateLabelFormatDay
                  )
                : '';
            }
          }
        },
        {
          id: 'tags',
          header: t('tags'),
          size: 350,
          accessorFn: originalRow => {
            return originalRow?.tags?.map(tag => ({
              label: tag.name,
              value: tag.id
            }));
          },
          type: 'tags'
        }
      ].filter(column => !disabledColumns.includes(column.id)),
    []
  );

  useEffect(() => {
    if (onChangeSelectedRows) onChangeSelectedRows(rowSelection);
  }, [rowSelection]);

  useEffect(() => {
    if (selectedRows && !isEqual(selectedRows, rowSelection)) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  return (
    <S.AddContactWrapper>
      <S.TableHeader>
        <S.TitleWrapper>
          <Typography variant="body2Bold">
            {translations.removeContactTitle || t('recipients')}
          </Typography>
          <VerticalDivider $height="24px" $margin="0 12px" />
          <Typography variant="body2">
            {translations.removeContactTotalCountText ? (
              <Trans
                i18nKey={translations.removeContactTotalCountText}
                values={{
                  count: totalContactData?.length || 0
                }}
                components={{ b: <b /> }}
              />
            ) : (
              <Trans
                i18nKey="totalRecipients"
                values={{ count: totalContactData?.length || 0 }}
                components={{ b: <b /> }}
              />
            )}
          </Typography>
        </S.TitleWrapper>
      </S.TableHeader>

      <Table
        columns={columns}
        data={contacts?.docs || []}
        globalFilters={globalFilters}
        pagination={{
          pageIndex,
          pageSize,
          total: filteredContactData?.length
        }}
        state={{
          totalContactData
        }}
        filterOptions={filterOptions?.docs?.map(doc => ({ label: doc.name, value: doc.id })) || []}
        manualPagination
        loading={isLoadingContacts || (searchedIds && isLoadingSearchedIds)}
        setGlobalFilters={setGlobalFilters}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        setPagination={setPagination}
        pageCount={pageCount}
        enabled={{ filter: true, header: true, perPageSelection: true }}
        translations={{
          selectedRowCountText: t('selectedRowCount', {
            selectedRowCount: rowSelectionArray.length,
            totalContactCount: filteredContactData?.length
          }),
          filteredWithText: t('filteredWithText', {
            filteredContactCount: filteredContactData?.length || '0'
          })
        }}
        filterProps={{
          placeholder: t('filterByType', { type: t('tags') }),
          searchPlaceholder: t('searchTagOnFilterPlaceholder')
        }}
        components={{
          SelectionHead: RemoveContactCustomSelectionHead,
          EmptyTable: table => <CustomRemoveContactEmptyTable {...table} />
        }}
      />
    </S.AddContactWrapper>
  );
}

export { RemoveContactTable };
